/**
 * @krisspy-file
 * @type page
 * @name "LocationForm"
 * @title "Location Form"
 * @description "Create or edit a location"
 * @routes ["/locations/create", "/locations/:id/edit"]
 * @layout "DashboardLayout"
 * @flowName "location-management"
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  AlertCircle,
  Info,
  Plus,
  Trash2,
  Upload,
  X,
  Loader2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import NumberInput from '../components/NumberInput';
import { uploadLocationImage } from '../lib/storage';
import { getLocationById, createLocation, updateLocation, upsertLocationTranslation, getLocationTranslations, deleteLocationTranslation } from '../services/locations.service';
import {
  getClosingPeriodsByLocationId,
  createClosingPeriod,
  updateClosingPeriod,
  deleteClosingPeriod,
  type ClosingPeriod
} from '../services/closingPeriods.service';
import {
  getEmailTextBlockByLocationAndType,
  type EmailTextBlock
} from '../services/emailTextBlocks.service';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';

// Quill toolbar configuration for basic HTML formatting
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ]
};

const quillFormats = [
  'bold', 'italic', 'underline',
  'list', 'bullet',
  'align',
  'link'
];
import type { Database } from '../lib/database.types';
import LoadingPlaceholder from '../components/LoadingPlaceholder';
import ConfirmationInfoBlocksEditor, { type InfoBlock } from '../components/ConfirmationInfoBlocksEditor';
import {
  CUSTOM_FIELD_DEFAULTS,
  CUSTOM_FIELD_ALLOWED_TAGS,
  labelEditorTextToHtml,
  labelHtmlToEditorText,
  sanitizeCustomFieldHtml,
  type CustomFieldConfig,
  type CustomFieldTranslation,
  type CustomFieldType,
} from '../lib/customFields';

type LocationInsert = Database['public']['Tables']['locations']['Insert'];
type LocationUpdate = Database['public']['Tables']['locations']['Update'];

interface TimeSlot {
  open: string;
  close: string;
}

interface DeliveryArea {
  id: string;
  name: string;
  price: number;
}

interface DeliveryPriceBracket {
  max: number;
  price: number;
}

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

const DAY_TRANSLATION_KEYS: Record<string, string> = {
  monday: 'locationForm.monday',
  tuesday: 'locationForm.tuesday',
  wednesday: 'locationForm.wednesday',
  thursday: 'locationForm.thursday',
  friday: 'locationForm.friday',
  saturday: 'locationForm.saturday',
  sunday: 'locationForm.sunday',
};

export default function LocationForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { merchantId, locationIds, isPlatformAdmin } = useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const { currentLanguage, defaultLanguage, availableLanguages } = useLanguage();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [activeLanguageTab, setActiveLanguageTab] = useState(defaultLanguage);
  const [cutOffUnit, setCutOffUnit] = useState<'hours' | 'minutes'>('hours');
  const [translations, setTranslations] = useState<Record<string, { location_name: string; pickup_address: string; additional_text_checkout: string; custom_fields: CustomFieldTranslation[]; confirmation_pickup_text: string; confirmation_info_blocks: InfoBlock[] | null }>>({});
  const [closingPeriods, setClosingPeriods] = useState<ClosingPeriod[]>([]);
  const [, setEmailInitial] = useState<Partial<EmailTextBlock> | null>(null);
  const [, setEmailCodes] = useState<Partial<EmailTextBlock> | null>(null);
  const [, setEmailDelivery] = useState<Partial<EmailTextBlock> | null>(null);

  const [formData, setFormData] = useState({
    // Tab 1: Basic Information
    location_name: '',
    location_slug: '',
    pickup_address: '',
    location_contact: '',
    picture_url: '',
    google_maps_url: '',
    rating: 0,
    opening_hours: {} as Record<string, { open: string; close: string } | null>,
    pickup_hours: {} as Record<string, TimeSlot[]>,
    timeslot_interval_minutes: 30,
    pay_later_enabled: false,

    // Tab 2: Display & Visibility
    display_order: 0,
    active: true,
    hidden: false,
    show_today_tomorrow: true,
    category_step_enabled: true,
    product_display_mode: 'mixed' as 'mixed' | 'grouped',
    additional_text_checkout: '',
    custom_fields: [] as CustomFieldConfig[],

    // Tab 3: Self-Service & Access (includes cut-off settings)
    self_service_enabled: false,
    gate_code: '',
    gate_lock_brand: '',
    two_emails_enabled: false,
    cut_off_codes: '',
    days_cut_off: 0,
    time_cut_off: '',

    // Tab 4: Email Settings
    from_name: '',
    reply_to_email: '',

    // Tab 5: Tax Configuration
    tax_rate: 0,
    tax_included: false,
    tax_name: '',

    // Tab 7: Confirmation Page
    confirmation_pickup_text: '',
    confirmation_info_blocks: null as InfoBlock[] | null,

    // Tab 6: Delivery Settings
    deliveries: false,
    delivery_areas: [] as DeliveryArea[],
    delivery_pre_booking_hours: 24,
    delivery_time_window_hours: 2,
    delivery_pricing_type: null as 'distance' | 'time' | null,
    delivery_distance_unit: 'km' as 'km' | 'mi',
    delivery_pricing_model: 'multiplication' as 'multiplication' | 'brackets',
    delivery_price_per_unit: 0,
    delivery_price_brackets: [] as DeliveryPriceBracket[],
    delivery_max_service_area: 0,
    delivery_origin: ''
  });

  useEffect(() => {
    if (isEditMode && id) {
      loadLocation();
    }
  }, [id, defaultLanguage]);

  async function loadLocation() {
    if (!id) return;

    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await getLocationById(id, {
      merchantId,
      locationIds,
      isPlatformAdmin
    }, defaultLanguage, defaultLanguage);

    if (fetchError) {
      setError(fetchError);
      setLoading(false);
      return;
    }

    if (data) {
      // Parse opening_hours from JSONB
      let openingHours: Record<string, { open: string; close: string } | null> = {};
      if (data.opening_hours && typeof data.opening_hours === 'object') {
        openingHours = data.opening_hours as any;
      }

      // Parse pickup_hours from JSONB
      let pickupHours: Record<string, TimeSlot[]> = {};
      if (data.pickup_hours && typeof data.pickup_hours === 'object') {
        pickupHours = data.pickup_hours as any;
      }

      // Parse delivery_areas from JSONB
      let deliveryAreas: any[] = [];
      if (data.delivery_areas) {
        deliveryAreas = Array.isArray(data.delivery_areas) ? data.delivery_areas : [];
      }

      // Parse delivery_price_brackets from JSONB
      let deliveryPriceBrackets: any[] = [];
      if (data.delivery_price_brackets) {
        deliveryPriceBrackets = Array.isArray(data.delivery_price_brackets) ? data.delivery_price_brackets : [];
      }

      setFormData({
        // Tab 1: Basic Information
        location_name: data.location_name || '',
        location_slug: data.location_slug || '',
        pickup_address: data.pickup_address || '',
        location_contact: data.location_contact || '',
        picture_url: data.picture_url || '',
        google_maps_url: data.google_maps_url || '',
        rating: data.rating || 0,
        opening_hours: openingHours,
        pickup_hours: pickupHours,
        timeslot_interval_minutes: data.timeslot_interval_minutes || 30,
        pay_later_enabled: data.pay_later_enabled || false,

        // Tab 2: Display & Visibility
        display_order: data.display_order || 0,
        active: data.active !== null ? data.active : true,
        hidden: data.hidden !== null ? data.hidden : false,
        show_today_tomorrow: data.show_today_tomorrow !== null ? data.show_today_tomorrow : true,
        category_step_enabled: data.category_step_enabled !== null ? data.category_step_enabled : true,
        product_display_mode: (data.product_display_mode as 'mixed' | 'grouped') || 'mixed',
        additional_text_checkout: (data as any).additional_text_checkout || '',
        custom_fields: Array.isArray((data as any).custom_fields) ? (data as any).custom_fields as CustomFieldConfig[] : [],

        // Tab 3: Self-Service & Access (includes cut-off settings)
        self_service_enabled: data.self_service_enabled || false,
        gate_code: data.gate_code || '',
        gate_lock_brand: data.gate_lock_brand || '',
        two_emails_enabled: data.two_emails_enabled || false,
        cut_off_codes: data.cut_off_codes || '',
        days_cut_off: data.days_cut_off || 0,
        time_cut_off: data.time_cut_off != null ? String(data.time_cut_off) : '',

        // Tab 4: Email Settings
        from_name: data.from_name || '',
        reply_to_email: data.reply_to_email || '',

        // Tab 5: Tax Configuration
        tax_rate: data.tax_rate || 0,
        tax_included: data.tax_included || false,
        tax_name: data.tax_name || '',

        // Tab 7: Confirmation Page
        confirmation_pickup_text: data.confirmation_pickup_text || '',
        confirmation_info_blocks: data.confirmation_info_blocks
          ? (Array.isArray(data.confirmation_info_blocks) ? data.confirmation_info_blocks as InfoBlock[] : null)
          : null,

        // Tab 6: Delivery Settings
        deliveries: data.deliveries || false,
        delivery_areas: deliveryAreas,
        delivery_pre_booking_hours: data.delivery_pre_booking_hours || 24,
        delivery_time_window_hours: data.delivery_time_window_hours || 2,
        delivery_pricing_type: (data.delivery_pricing_type as 'distance' | 'time' | null) || null,
        delivery_distance_unit: (data.delivery_distance_unit as any) || 'km',
        delivery_pricing_model: (data.delivery_pricing_model as any) || 'multiplication',
        delivery_price_per_unit: data.delivery_price_per_unit || 0,
        delivery_price_brackets: deliveryPriceBrackets,
        delivery_max_service_area: data.delivery_max_service_area || 0,
        delivery_origin: data.delivery_origin || ''
      });

      // Auto-detect cut-off unit: show as minutes if value is under 1 hour
      const timeCutOffHours = parseFloat(String(data.time_cut_off));
      if (!isNaN(timeCutOffHours) && timeCutOffHours > 0 && timeCutOffHours < 1) {
        setCutOffUnit('minutes');
      }

      // Load closing periods
      const { data: periodsData, error: periodsError} = await getClosingPeriodsByLocationId(id);
      if (!periodsError && periodsData) {
        setClosingPeriods(periodsData);
      }

      // Load email text blocks
      const { data: emailInitialData } = await getEmailTextBlockByLocationAndType(id, 1);
      if (emailInitialData) setEmailInitial(emailInitialData);

      const { data: emailCodesData } = await getEmailTextBlockByLocationAndType(id, 2);
      if (emailCodesData) setEmailCodes(emailCodesData);

      const { data: emailDeliveryData } = await getEmailTextBlockByLocationAndType(id, 3);
      if (emailDeliveryData) setEmailDelivery(emailDeliveryData);

      // Load translations
      const { data: translationsData } = await getLocationTranslations(id);
      if (translationsData && translationsData.length > 0) {
        const translationsMap: Record<string, { location_name: string; pickup_address: string; additional_text_checkout: string; custom_fields: CustomFieldTranslation[]; confirmation_pickup_text: string; confirmation_info_blocks: InfoBlock[] | null }> = {};
        for (const t of translationsData) {
          translationsMap[t.language_code] = {
            location_name: t.location_name || '',
            pickup_address: t.pickup_address || '',
            additional_text_checkout: (t as any).additional_text_checkout || '',
            custom_fields: Array.isArray((t as any).custom_fields) ? (t as any).custom_fields as CustomFieldTranslation[] : [],
            confirmation_pickup_text: t.confirmation_pickup_text || '',
            confirmation_info_blocks: t.confirmation_info_blocks
              ? (Array.isArray(t.confirmation_info_blocks) ? t.confirmation_info_blocks as InfoBlock[] : null)
              : null,
          };
        }
        setTranslations(translationsMap);
      }
    }

    setLoading(false);
  }

  function generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function handleNameChange(name: string) {
    setFormData(prev => ({
      ...prev,
      location_name: name,
      location_slug: prev.location_slug || generateSlug(name)
    }));
  }

  function validateForm(): Record<string, string> {
    const errors: Record<string, string> = {};

    if (!formData.location_name.trim()) {
      errors.location_name = t('locationForm.nameRequired');
    }

    if (!formData.location_slug.trim()) {
      errors.location_slug = t('locationForm.slugRequired');
    } else if (!/^[a-z0-9-]+$/.test(formData.location_slug)) {
      errors.location_slug = t('locationForm.slugInvalid');
    }

    if (!formData.pickup_address.trim()) {
      errors.pickup_address = t('locationForm.addressRequired');
    }

    // Validate time slots
    Object.entries(formData.opening_hours).forEach(([day, hours]) => {
      if (hours && hours.open && hours.close && hours.open >= hours.close) {
        errors[`opening_hours_${day}`] = t('locationForm.openBeforeClose', { day: t(DAY_TRANSLATION_KEYS[day]) });
      }
    });

    Object.entries(formData.pickup_hours).forEach(([day, slots]) => {
      slots.forEach((slot, index) => {
        if (slot.open && slot.close && slot.open >= slot.close) {
          errors[`pickup_hours_${day}_${index}`] = t('locationForm.slotOpenBeforeClose', { day: t(DAY_TRANSLATION_KEYS[day]), slot: index + 1 });
        }
      });
    });

    // Validate delivery configuration: only the editor relevant to the
    // chosen pricing mode (area-based vs variable+brackets) is enforced.
    if (formData.deliveries) {
      if (formData.delivery_pricing_type === null) {
        // Area mode
        const areaIds = new Set<string>();
        formData.delivery_areas.forEach((area, index) => {
          if (!area.id.trim()) {
            errors[`delivery_area_${index}_id`] = `Area ${index + 1}: ID is required`;
          } else if (areaIds.has(area.id)) {
            errors[`delivery_area_${index}_id`] = `Area ${index + 1}: Duplicate ID "${area.id}"`;
          } else {
            areaIds.add(area.id);
          }
          if (!area.name.trim()) {
            errors[`delivery_area_${index}_name`] = `Area ${index + 1}: Name is required`;
          }
          if (area.price < 0) {
            errors[`delivery_area_${index}_price`] = `Area ${index + 1}: Price cannot be negative`;
          }
        });
      } else if (formData.delivery_pricing_model === 'brackets') {
        // Variable mode + brackets curve
        formData.delivery_price_brackets.forEach((bracket, index) => {
          if (bracket.max <= 0) {
            errors[`price_bracket_${index}_max`] = `Bracket ${index + 1}: Max must be greater than 0`;
          }
          if (bracket.price < 0) {
            errors[`price_bracket_${index}_price`] = `Bracket ${index + 1}: Price cannot be negative`;
          }
          if (index > 0 && bracket.max <= formData.delivery_price_brackets[index - 1].max) {
            errors[`price_bracket_${index}_max`] = `Bracket ${index + 1}: Max must be greater than previous bracket`;
          }
        });
      }
    }

    setValidationErrors(errors);
    return errors;
  }

  async function handleImageUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const publicUrl = await uploadLocationImage(file, merchantId || 'default');
      setFormData(prev => ({ ...prev, picture_url: publicUrl }));
    } catch (err: any) {
      console.error('Error uploading image:', err);
      showToast(err.message || 'Failed to upload image', 'error');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  }

  function handleRemoveImage() {
    setFormData(prev => ({ ...prev, picture_url: '' }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      // Determine which tab(s) have errors and tell the user
      const errorKeys = Object.keys(errors);
      const tabsWithErrors: string[] = [];
      if (errorKeys.some(k => ['location_name', 'location_slug', 'pickup_address'].includes(k) || k.startsWith('opening_hours') || k.startsWith('pickup_hours'))) {
        tabsWithErrors.push(t('locationForm.tabBasicInfo'));
      }
      if (errorKeys.some(k => k.startsWith('delivery_area') || k.startsWith('price_bracket'))) {
        tabsWithErrors.push(t('locationForm.tabDelivery'));
      }
      const detail = tabsWithErrors.length > 0
        ? ` ${t('locationForm.check')}: ${tabsWithErrors.join(', ')}`
        : '';
      showToast(`${t('locationForm.fixValidationErrors')}${detail}`, 'error');
      return;
    }

    if (!merchantId) {
      setError(t('locationForm.merchantIdRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
    // Prepare data object with all fields
    const dataToSave: any = {
      // Tab 1: Basic Information
      location_name: formData.location_name,
      location_slug: formData.location_slug,
      pickup_address: formData.pickup_address,
      location_contact: formData.location_contact || null,
      picture_url: formData.picture_url || null,
      google_maps_url: formData.google_maps_url || null,
      rating: formData.rating || null,
      opening_hours: formData.opening_hours,
      pickup_hours: formData.pickup_hours,
      timeslot_interval_minutes: formData.timeslot_interval_minutes,
      pay_later_enabled: formData.pay_later_enabled,

      // Tab 2: Display & Visibility
      display_order: formData.display_order,
      active: formData.active,
      hidden: formData.hidden,
      show_today_tomorrow: formData.show_today_tomorrow,
      category_step_enabled: formData.category_step_enabled,
      product_display_mode: formData.product_display_mode,
      additional_text_checkout: formData.additional_text_checkout || null,
      custom_fields: formData.custom_fields.length > 0 ? formData.custom_fields : null,

      // Tab 3: Self-Service & Access (includes cut-off settings)
      self_service_enabled: formData.self_service_enabled,
      gate_code: formData.gate_code || null,
      gate_lock_brand: formData.gate_lock_brand || null,
      two_emails_enabled: formData.two_emails_enabled,
      cut_off_codes: formData.cut_off_codes || null,
      days_cut_off: formData.days_cut_off || null,
      time_cut_off: formData.time_cut_off || null,

      // Tab 4: Email Settings
      from_name: formData.from_name || null,
      reply_to_email: formData.reply_to_email || null,

      // Tab 5: Tax Configuration
      tax_rate: formData.tax_rate || null,
      tax_included: formData.tax_included,
      tax_name: formData.tax_name || null,

      // Tab 7: Confirmation Page
      confirmation_pickup_text: formData.confirmation_pickup_text || null,
      confirmation_info_blocks: formData.confirmation_info_blocks || null,

      // Tab 6: Delivery Settings - set all to null if deliveries is disabled
      deliveries: formData.deliveries,
      delivery_areas: formData.deliveries ? formData.delivery_areas : null,
      delivery_pre_booking_hours: formData.deliveries ? (formData.delivery_pre_booking_hours || null) : null,
      delivery_time_window_hours: formData.deliveries ? (formData.delivery_time_window_hours || null) : null,
      delivery_pricing_type: formData.deliveries ? (formData.delivery_pricing_type || null) : null,
      delivery_distance_unit: formData.deliveries ? (formData.delivery_distance_unit || null) : null,
      delivery_pricing_model: formData.deliveries ? (formData.delivery_pricing_model || null) : null,
      delivery_price_per_unit: formData.deliveries ? (formData.delivery_price_per_unit || null) : null,
      delivery_price_brackets: formData.deliveries ? formData.delivery_price_brackets : null,
      delivery_max_service_area: formData.deliveries ? (formData.delivery_max_service_area || null) : null,
      delivery_origin: formData.deliveries ? (formData.delivery_origin || null) : null
    };

    if (isEditMode && id) {
      const updateData: LocationUpdate = {
        ...dataToSave,
        updated_at: new Date().toISOString()
      };

      const { error: updateError } = await updateLocation(
        id,
        updateData
      );

      if (updateError) {
        showToast(t('locationForm.failedToUpdate') + updateError, 'error');
        setSaving(false);
        return;
      }

      // Clean up any stale default-language translation row so it can't
      // override the freshly-updated main-table value on later reads.
      if (defaultLanguage) {
        await deleteLocationTranslation(id, defaultLanguage);
      }

      // Save closing periods
      for (const period of closingPeriods) {
        if (period.id.startsWith('temp-')) {
          // Create new closing period
          const { error: createPeriodError } = await createClosingPeriod({
            location_id: id,
            start_date: period.start_date,
            end_date: period.end_date,
            reason: period.reason || null
          });
          if (createPeriodError) {
            console.error('Failed to create closing period:', createPeriodError);
          }
        } else {
          // Update existing closing period
          const { error: updatePeriodError } = await updateClosingPeriod(period.id, {
            start_date: period.start_date,
            end_date: period.end_date,
            reason: period.reason || null
          });
          if (updatePeriodError) {
            console.error('Failed to update closing period:', updatePeriodError);
          }
        }
      }

      // Save translations for all languages (except default). A language whose
      // fields are all empty is deleted so clearing a translation persists
      // instead of leaving the previous row in place.
      for (const [langCode, translationData] of Object.entries(translations)) {
        if (langCode === defaultLanguage) continue;
        const hasContent = translationData.location_name || translationData.pickup_address || translationData.additional_text_checkout || translationData.custom_fields?.length || translationData.confirmation_pickup_text || translationData.confirmation_info_blocks;
        if (hasContent) {
          const { error: translationError } = await upsertLocationTranslation(
            id,
            langCode,
            {
              location_name: translationData.location_name || null,
              pickup_address: translationData.pickup_address || null,
              additional_text_checkout: translationData.additional_text_checkout || null,
              custom_fields: translationData.custom_fields?.length > 0 ? translationData.custom_fields : null,
              confirmation_pickup_text: translationData.confirmation_pickup_text || null,
              confirmation_info_blocks: translationData.confirmation_info_blocks || null,
            }
          );
          if (translationError) {
            console.error(`Failed to save ${langCode} translation:`, translationError);
          }
        } else {
          const { error: translationError } = await deleteLocationTranslation(id, langCode);
          if (translationError) {
            console.error(`Failed to delete ${langCode} translation:`, translationError);
          }
        }
      }

      showToast(t('locationForm.updatedSuccess'), 'success');
      navigate('/locations');
    } else {
      const insertData: LocationInsert = {
        merchant_id: merchantId,
        ...dataToSave
      };

      const { data: newLocation, error: createError } = await createLocation(
        insertData
      );

      if (createError) {
        showToast(t('locationForm.failedToCreate') + createError, 'error');
        setSaving(false);
        return;
      }

      if (newLocation) {
        // Save closing periods for the new location
        for (const period of closingPeriods) {
          if (period.id.startsWith('temp-')) {
            const { error: createPeriodError } = await createClosingPeriod({
              location_id: newLocation.id,
              start_date: period.start_date,
              end_date: period.end_date,
              reason: period.reason || null
            });
            if (createPeriodError) {
              console.error('Failed to create closing period:', createPeriodError);
            }
          }
        }

        // Save translations for all languages (except default)
        for (const [langCode, translationData] of Object.entries(translations)) {
          if (langCode !== defaultLanguage && (translationData.location_name || translationData.pickup_address || translationData.additional_text_checkout || translationData.custom_fields?.length || translationData.confirmation_pickup_text || translationData.confirmation_info_blocks)) {
            const { error: translationError } = await upsertLocationTranslation(
              newLocation.id,
              langCode,
              {
                location_name: translationData.location_name || null,
                pickup_address: translationData.pickup_address || null,
                additional_text_checkout: translationData.additional_text_checkout || null,
                custom_fields: translationData.custom_fields?.length > 0 ? translationData.custom_fields : null,
                confirmation_pickup_text: translationData.confirmation_pickup_text || null,
                confirmation_info_blocks: translationData.confirmation_info_blocks || null,
              }
            );
            if (translationError) {
              console.error(`Failed to save ${langCode} translation:`, translationError);
            }
          }
        }

        showToast(t('locationForm.createdSuccess'), 'success');
        navigate('/locations');
      } else {
        navigate('/locations');
      }
    }
    } catch (err) {
      console.error('Error saving location:', err);
      showToast(t('locationForm.errorSaving'), 'error');
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-2xl animate-fade-in">
        <div className="flex items-center gap-md" data-testid="location-form-loading-header">
          <button onClick={() => navigate('/locations')} className="btn-icon" data-testid="location-form-loading-back-button">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-primary">{t('common.loading')}</h1>
        </div>
        <LoadingPlaceholder />
      </div>
    );
  }

  if (error && isEditMode) {
    return (
      <div className="space-y-2xl animate-fade-in">
        <div className="flex items-center gap-md" data-testid="location-form-error-header">
          <button onClick={() => navigate('/locations')} className="btn-icon" data-testid="location-form-error-back-button">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-primary">{t('common.error')}</h1>
        </div>
        <div className="card">
          <div className="text-center py-2xl">
            <div className="w-16 h-16 rounded-full bg-danger/10 mx-auto mb-lg flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-danger" />
            </div>
            <h3 className="text-lg font-semibold text-primary mb-sm">{t('locationForm.notFound')}</h3>
            <p className="text-secondary mb-xl">{error}</p>
            <button onClick={() => navigate('/locations')} className="btn btn-primary">
              {t('locationForm.backToLocations')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper functions for opening hours
  const updateOpeningHours = (day: string, field: 'open' | 'close', value: string) => {
    setFormData(prev => ({
      ...prev,
      opening_hours: {
        ...prev.opening_hours,
        [day]: prev.opening_hours[day]
          ? { ...prev.opening_hours[day]!, [field]: value }
          : { open: field === 'open' ? value : '', close: field === 'close' ? value : '' }
      }
    }));
  };

  const toggleDayClosed = (day: string) => {
    setFormData(prev => ({
      ...prev,
      opening_hours: {
        ...prev.opening_hours,
        [day]: prev.opening_hours[day] ? null : { open: '09:00', close: '17:00' }
      }
    }));
  };

  // Helper functions for pickup hours
  const addPickupSlot = (day: string) => {
    setFormData(prev => ({
      ...prev,
      pickup_hours: {
        ...prev.pickup_hours,
        [day]: [...(prev.pickup_hours[day] || []), { open: '09:00', close: '17:00' }]
      }
    }));
  };

  const removePickupSlot = (day: string, index: number) => {
    setFormData(prev => {
      const remaining = prev.pickup_hours[day].filter((_, i) => i !== index);
      if (remaining.length === 0) {
        const { [day]: _, ...rest } = prev.pickup_hours;
        return { ...prev, pickup_hours: rest };
      }
      return { ...prev, pickup_hours: { ...prev.pickup_hours, [day]: remaining } };
    });
  };

  const updatePickupSlot = (day: string, index: number, field: 'open' | 'close', value: string) => {
    setFormData(prev => ({
      ...prev,
      pickup_hours: {
        ...prev.pickup_hours,
        [day]: prev.pickup_hours[day].map((slot, i) =>
          i === index ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  // Helper functions for closing periods
  const addClosingPeriod = () => {
    const newPeriod: Partial<ClosingPeriod> = {
      id: `temp-${Date.now()}`,
      location_id: id || '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0],
      reason: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    setClosingPeriods(prev => [...prev, newPeriod as ClosingPeriod]);
  };

  const removeClosingPeriod = async (periodId: string) => {
    // If it's a temporary ID (not yet saved), just remove from state
    if (periodId.startsWith('temp-')) {
      setClosingPeriods(prev => prev.filter(p => p.id !== periodId));
      return;
    }

    // Otherwise, delete from database
    const { error } = await deleteClosingPeriod(periodId);
    if (error) {
      showToast(t('locationForm.failedToDeleteClosingPeriod') + error, 'error');
      return;
    }
    setClosingPeriods(prev => prev.filter(p => p.id !== periodId));
  };

  const updateClosingPeriodField = (index: number, field: keyof ClosingPeriod, value: string) => {
    setClosingPeriods(prev => prev.map((period, i) =>
      i === index ? { ...period, [field]: value } : period
    ));
  };

  // Helper functions for delivery areas
  const addDeliveryArea = () => {
    setFormData(prev => ({
      ...prev,
      delivery_areas: [...prev.delivery_areas, { id: '', name: '', price: 0 }]
    }));
  };

  const removeDeliveryArea = (index: number) => {
    setFormData(prev => ({
      ...prev,
      delivery_areas: prev.delivery_areas.filter((_, i) => i !== index)
    }));
  };

  const updateDeliveryArea = (index: number, field: keyof DeliveryArea, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      delivery_areas: prev.delivery_areas.map((area, i) =>
        i === index ? { ...area, [field]: value } : area
      )
    }));
  };

  // Helper functions for delivery price brackets
  const addPriceBracket = () => {
    setFormData(prev => ({
      ...prev,
      delivery_price_brackets: [...prev.delivery_price_brackets, { max: 0, price: 0 }]
    }));
  };

  const removePriceBracket = (index: number) => {
    setFormData(prev => ({
      ...prev,
      delivery_price_brackets: prev.delivery_price_brackets.filter((_, i) => i !== index)
    }));
  };

  const updatePriceBracket = (index: number, field: keyof DeliveryPriceBracket, value: number) => {
    setFormData(prev => ({
      ...prev,
      delivery_price_brackets: prev.delivery_price_brackets.map((bracket, i) =>
        i === index ? { ...bracket, [field]: value } : bracket
      )
    }));
  };

  const tabs = [
    { id: 'basic', label: t('locationForm.tabBasicInfo') },
    { id: 'display', label: t('locationForm.tabDisplay') },
    { id: 'selfservice', label: t('locationForm.tabSelfService') },
    { id: 'email', label: t('locationForm.tabEmail') },
    { id: 'tax', label: t('locationForm.tabTax') },
    { id: 'delivery', label: t('locationForm.tabDelivery') },
    { id: 'confirmation', label: t('locationForm.tabConfirmation') }
  ];

  return (
    <div className="space-y-2xl animate-fade-in">
      <div className="page-header" data-testid="location-form-page-header">
        <button
          data-testid="location-form-back-button"
          onClick={() => navigate('/locations')}
          className="btn-icon"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="page-header-title flex-1">
          <h1 className="text-3xl font-bold text-primary mb-xs">
            {isEditMode ? t('locationForm.editLocation') : t('locationForm.createLocation')}
          </h1>
          <p className="text-secondary">
            {isEditMode ? t('locationForm.updateLocationInfo') : t('locationForm.addNewLocation')}
          </p>
        </div>
        <div className="page-header-actions" data-testid="location-form-page-header-actions">
          <button
            type="button"
            onClick={() => navigate('/locations')}
            className="btn btn-ghost"
            data-testid="location-form-cancel-button"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={saving}
            data-testid="location-form-save-button"
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex space-x-lg overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`pb-md px-sm whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue text-blue font-medium'
                  : 'border-transparent text-secondary hover:text-primary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-xl">
        {error && !isEditMode && (
          <div className="p-md bg-danger/10 border border-danger/20 rounded-lg">
            <p className="text-danger text-sm">{error}</p>
          </div>
        )}

        {/* Validation error summary — visible on all tabs */}
        {Object.keys(validationErrors).length > 0 && (
          <div className="p-md bg-danger/10 border border-danger/20 rounded-lg">
            <p className="text-danger text-sm font-medium mb-xs">{t('locationForm.pleaseFixErrors')}</p>
            <ul className="list-disc list-inside text-danger text-sm space-y-0.5">
              {Object.values(validationErrors).map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Tab 1: Basic Information */}
        {activeTab === 'basic' && (
          <div className="card">
            <h2 className="text-lg font-semibold text-primary mb-lg">{t('locationForm.tabBasicInfo')}</h2>
            <div className="space-y-lg">
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.locationName')} *
                </label>
                <input
                  type="text"
                  value={formData.location_name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={`input w-full ${validationErrors.location_name ? 'border-danger' : ''}`}
                  placeholder={t('locationForm.locationNamePlaceholder')}
                  required
                />
                {validationErrors.location_name && (
                  <p className="text-danger text-sm mt-xs">{validationErrors.location_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.locationSlug')} *
                </label>
                <input
                  type="text"
                  value={formData.location_slug}
                  onChange={(e) => setFormData({ ...formData, location_slug: e.target.value })}
                  className={`input w-full ${validationErrors.location_slug ? 'border-danger' : ''}`}
                  placeholder={t('locationForm.locationSlugPlaceholder')}
                  required
                />
                {validationErrors.location_slug && (
                  <p className="text-danger text-sm mt-xs">{validationErrors.location_slug}</p>
                )}
                <p className="text-xs text-secondary mt-xs">
                  {t('locationForm.slugHelp')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.pickupAddress')} *
                </label>
                <div className={validationErrors.pickup_address ? 'border border-danger rounded-lg' : ''}>
                  <textarea
                    value={formData.pickup_address}
                    onChange={(e) => setFormData({ ...formData, pickup_address: e.target.value })}
                    className="input w-full"
                    rows={3}
                    placeholder={t('locationForm.pickupAddressPlaceholder')}
                  />
                </div>
                {validationErrors.pickup_address && (
                  <p className="text-danger text-sm mt-xs">{validationErrors.pickup_address}</p>
                )}
              </div>

              {/* Multi-language translations section */}
              {availableLanguages.length > 1 && (
                <div className="border-t border-primary pt-lg">
                  <h3 className="text-lg font-semibold text-primary mb-md">{t('locationForm.translations')}</h3>

                  {/* Language tabs */}
                  <div className="flex gap-sm mb-lg overflow-x-auto">
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setActiveLanguageTab(lang.code)}
                        className={`px-md py-sm rounded-md flex items-center gap-xs whitespace-nowrap transition-colors ${
                          activeLanguageTab === lang.code
                            ? 'bg-blue text-white'
                            : 'bg-secondary text-secondary hover:bg-primary/10'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="space-y-md">
                    {activeLanguageTab === defaultLanguage ? (
                      /* Show main table data for default language */
                      <div className="p-md bg-blue/5 border border-blue/20 rounded-lg">
                        <p className="text-sm text-blue">
                          <strong>{t('locationForm.locationName')}:</strong> {formData.location_name || <em className="text-secondary">{t('common.none')}</em>}
                        </p>
                        <div className="text-sm text-blue mt-sm">
                          <strong>{t('locationForm.pickupAddress')}:</strong>{' '}
                          {formData.pickup_address ? (
                            <span dangerouslySetInnerHTML={{ __html: formData.pickup_address }} />
                          ) : (
                            <em className="text-secondary">{t('common.none')}</em>
                          )}
                        </div>
                        <p className="text-xs text-secondary mt-md">
                          {t('locationForm.mainLanguageEditAbove')}
                        </p>
                      </div>
                    ) : (
                      /* Show translation fields for other languages */
                      <>
                        <div>
                          <label className="block text-sm font-medium text-primary mb-sm">
                            {t('locationForm.locationName')} ({activeLanguageTab.toUpperCase()})
                          </label>
                          <input
                            type="text"
                            value={translations[activeLanguageTab]?.location_name || ''}
                            onChange={(e) => setTranslations(prev => ({
                              ...prev,
                              [activeLanguageTab]: {
                                ...prev[activeLanguageTab],
                                location_name: e.target.value,
                              }
                            }))}
                            className="input w-full"
                            placeholder={`Add the ${availableLanguages.find(l => l.code === activeLanguageTab)?.label} version`}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-primary mb-sm">
                            {t('locationForm.pickupAddress')} ({activeLanguageTab.toUpperCase()})
                          </label>
                          <textarea
                            value={translations[activeLanguageTab]?.pickup_address || ''}
                            onChange={(e) => setTranslations(prev => ({
                              ...prev,
                              [activeLanguageTab]: {
                                ...prev[activeLanguageTab],
                                pickup_address: e.target.value,
                              }
                            }))}
                            className="input w-full"
                            rows={3}
                            placeholder={`Add the ${availableLanguages.find(l => l.code === activeLanguageTab)?.label} version`}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.contactInfo')}
                </label>
                <input
                  type="text"
                  value={formData.location_contact}
                  onChange={(e) => setFormData({ ...formData, location_contact: e.target.value })}
                  className="input w-full"
                  placeholder={t('locationForm.contactInfoPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.pictureUrl')}
                </label>

                {formData.picture_url ? (
                  <div className="relative group">
                    <img
                      src={formData.picture_url}
                      alt="Location"
                      className="w-full h-48 object-contain rounded-lg"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-1 bg-danger text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className={`flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    uploadingImage
                      ? 'border-blue/50 bg-blue/5'
                      : 'border-primary/20 hover:border-blue/50 hover:bg-blue/5'
                  }`}>
                    <div className="flex flex-col items-center justify-center py-lg">
                      {uploadingImage ? (
                        <>
                          <Loader2 className="w-8 h-8 text-blue animate-spin mb-sm" />
                          <p className="text-sm text-secondary">{t('common.uploading', 'Uploading...')}</p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-secondary mb-sm" />
                          <p className="text-sm text-secondary">
                            {t('locationForm.uploadImage', 'Click to upload an image')}
                          </p>
                          <p className="text-xs text-secondary mt-xs">PNG, JPG, WebP (max 10MB)</p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                  </label>
                )}

                {!formData.picture_url && !uploadingImage && (
                  <div className="mt-sm">
                    <p className="text-xs text-secondary mb-xs">{t('locationForm.orPasteUrl', 'Or paste an image URL')}</p>
                    <input
                      type="url"
                      value={formData.picture_url}
                      onChange={(e) => setFormData({ ...formData, picture_url: e.target.value })}
                      className="input w-full text-sm"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.googleMapsUrl')}
                </label>
                <input
                  type="url"
                  value={formData.google_maps_url}
                  onChange={(e) => setFormData({ ...formData, google_maps_url: e.target.value })}
                  className="input w-full"
                  placeholder="https://maps.google.com/..."
                />
                <p className="text-xs text-secondary mt-xs">
                  {t('locationForm.googleMapsUrlHelp')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.rating')}
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="5"
                  value={formData.rating}
                  onChange={(e) => setFormData({ ...formData, rating: parseFloat(e.target.value) || 0 })}
                  className="input w-full"
                  placeholder="0.0"
                />
                <p className="text-xs text-secondary mt-xs">
                  {t('locationForm.ratingHelp')}
                </p>
              </div>

              <div>
                <label className="flex items-center gap-md">
                  <input
                    type="checkbox"
                    checked={formData.pay_later_enabled}
                    onChange={(e) => setFormData({ ...formData, pay_later_enabled: e.target.checked })}
                    className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  />
                  <span className="text-sm font-medium text-primary">{t('locationForm.payLaterEnabled')}</span>
                </label>
              </div>

              {/* Opening Hours Editor */}
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.openingHours')}
                </label>
                <div className="space-y-sm border border-border rounded-lg p-md">
                  {DAYS_OF_WEEK.map((day) => {
                    const hours = formData.opening_hours[day];
                    const isClosed = !hours;
                    return (
                      <div key={day} className="flex items-center gap-md">
                        <div className="w-24">
                          <span className="text-sm font-medium text-primary">{t(DAY_TRANSLATION_KEYS[day])}</span>
                        </div>
                        <label className="flex items-center gap-sm w-20">
                          <input
                            type="checkbox"
                            checked={isClosed}
                            onChange={() => toggleDayClosed(day)}
                            className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                          />
                          <span className="text-xs text-secondary">{t('locationForm.closed')}</span>
                        </label>
                        {!isClosed && (
                          <>
                            <input
                              type="time"
                              value={hours?.open || '09:00'}
                              onChange={(e) => updateOpeningHours(day, 'open', e.target.value)}
                              className="input text-sm w-32"
                            />
                            <span className="text-secondary">{t('locationForm.to')}</span>
                            <input
                              type="time"
                              value={hours?.close || '17:00'}
                              onChange={(e) => updateOpeningHours(day, 'close', e.target.value)}
                              className="input text-sm w-32"
                            />
                          </>
                        )}
                        {validationErrors[`opening_hours_${day}`] && (
                          <p className="text-danger text-xs">{validationErrors[`opening_hours_${day}`]}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Closing Periods Editor */}
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.closingPeriods')}
                </label>
                <p className="text-xs text-secondary mb-sm">
                  {t('locationForm.closingPeriodsHelp')}
                </p>
                <div className="space-y-sm border border-border rounded-lg p-md">
                  {closingPeriods.length === 0 ? (
                    <p className="text-xs text-secondary italic">{t('locationForm.noClosingPeriods')}</p>
                  ) : (
                    closingPeriods.map((period, index) => (
                      <div key={period.id} className="flex items-start gap-md p-sm bg-secondary rounded-lg">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-md">
                          <div>
                            <label className="block text-xs font-medium text-secondary mb-xs">
                              {t('locationForm.startDate')}
                            </label>
                            <input
                              type="date"
                              value={period.start_date}
                              onChange={(e) => updateClosingPeriodField(index, 'start_date', e.target.value)}
                              className="input text-sm w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-secondary mb-xs">
                              {t('locationForm.endDate')}
                            </label>
                            <input
                              type="date"
                              value={period.end_date}
                              onChange={(e) => updateClosingPeriodField(index, 'end_date', e.target.value)}
                              className="input text-sm w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-secondary mb-xs">
                              {t('locationForm.reasonOptional')}
                            </label>
                            <input
                              type="text"
                              value={period.reason || ''}
                              onChange={(e) => updateClosingPeriodField(index, 'reason', e.target.value)}
                              placeholder={t('locationForm.reasonPlaceholder')}
                              className="input text-sm w-full"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeClosingPeriod(period.id)}
                          className="btn-icon btn-sm text-danger mt-6"
                          title={t('locationForm.removeClosingPeriod')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                  <button
                    type="button"
                    onClick={addClosingPeriod}
                    className="btn btn-ghost w-full"
                  >
                    <Plus className="w-4 h-4" />
                    {t('locationForm.addClosingPeriod')}
                  </button>
                </div>
              </div>

              {/* Pickup Hours Editor */}
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.pickupHours')}
                </label>
                <p className="text-xs text-secondary mb-sm">
                  {t('locationForm.pickupHoursHelp')}
                </p>
                <div className="space-y-md border border-border rounded-lg p-md">
                  {DAYS_OF_WEEK.map((day) => {
                    const slots = formData.pickup_hours[day] || [];
                    return (
                      <div key={day} className="space-y-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-primary">{t(DAY_TRANSLATION_KEYS[day])}</span>
                          <button
                            type="button"
                            onClick={() => addPickupSlot(day)}
                            className="btn-icon btn-sm"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        {slots.length === 0 && (
                          <p className="text-xs text-secondary italic">{t('locationForm.noPickupSlots')}</p>
                        )}
                        {slots.map((slot, index) => (
                          <div key={index} className="flex items-center gap-sm ml-md">
                            <input
                              type="time"
                              value={slot.open}
                              onChange={(e) => updatePickupSlot(day, index, 'open', e.target.value)}
                              className="input text-sm w-32"
                            />
                            <span className="text-secondary">{t('locationForm.to')}</span>
                            <input
                              type="time"
                              value={slot.close}
                              onChange={(e) => updatePickupSlot(day, index, 'close', e.target.value)}
                              className="input text-sm w-32"
                            />
                            <button
                              type="button"
                              onClick={() => removePickupSlot(day, index)}
                              className="btn-icon btn-sm text-danger"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            {validationErrors[`pickup_hours_${day}_${index}`] && (
                              <p className="text-danger text-xs">{validationErrors[`pickup_hours_${day}_${index}`]}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.timeslotInterval')}
                </label>
                <NumberInput
                  value={formData.timeslot_interval_minutes}
                  onChange={(v) => setFormData({ ...formData, timeslot_interval_minutes: v || 30 })}
                  mode="int"
                  min={15}
                  step={15}
                  className="input w-full"
                  data-testid="location-form-timeslot-interval-input"
                />
                <p className="text-xs text-secondary mt-xs">
                  {t('locationForm.timeslotIntervalHelp')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Display & Visibility */}
        {activeTab === 'display' && (
          <div className="card">
            <h2 className="text-lg font-semibold text-primary mb-lg">{t('locationForm.tabDisplay')}</h2>
            <div className="space-y-lg">
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.displayOrder')}
                </label>
                <NumberInput
                  value={formData.display_order}
                  onChange={(v) => setFormData({ ...formData, display_order: v || 0 })}
                  mode="int"
                  min={0}
                  className="input w-full"
                />
                <p className="text-xs text-secondary mt-xs">
                  {t('locationForm.displayOrderHelp')}
                </p>
              </div>

              <div>
                <label className="flex items-center gap-md">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  />
                  <span className="text-sm font-medium text-primary">{t('common.active')}</span>
                </label>
              </div>

              <div>
                <label className="flex items-center gap-md">
                  <input
                    type="checkbox"
                    checked={formData.hidden}
                    onChange={(e) => setFormData({ ...formData, hidden: e.target.checked })}
                    className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  />
                  <span className="text-sm font-medium text-primary">{t('locationForm.hidden')}</span>
                </label>
              </div>

              <div>
                <label className="flex items-center gap-md">
                  <input
                    type="checkbox"
                    checked={formData.show_today_tomorrow}
                    onChange={(e) => setFormData({ ...formData, show_today_tomorrow: e.target.checked })}
                    className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  />
                  <span className="text-sm font-medium text-primary">{t('locationForm.showTodayTomorrow')}</span>
                </label>
              </div>

              <div>
                <label className="flex items-center gap-md">
                  <input
                    type="checkbox"
                    checked={formData.category_step_enabled}
                    onChange={(e) => setFormData({ ...formData, category_step_enabled: e.target.checked })}
                    className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  />
                  <span className="text-sm font-medium text-primary">{t('locationForm.categoryStepEnabled')}</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.productDisplayMode')}
                </label>
                <select
                  value={formData.product_display_mode}
                  onChange={(e) => setFormData({ ...formData, product_display_mode: e.target.value as 'mixed' | 'grouped' })}
                  className="input w-full"
                >
                  <option value="mixed">{t('locationForm.mixed')}</option>
                  <option value="grouped">{t('locationForm.grouped')}</option>
                </select>
              </div>

              {/* Additional Checkout Text */}
              <div className="border-t border-primary pt-lg">
                <h3 className="text-lg font-semibold text-primary mb-md">{t('locationForm.checkoutSettings')}</h3>

                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('locationForm.additionalTextCheckout')}
                  </label>
                  <ReactQuill
                    value={formData.additional_text_checkout}
                    onChange={(value) => setFormData({ ...formData, additional_text_checkout: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    theme="snow"
                    placeholder={t('locationForm.additionalTextCheckoutPlaceholder')}
                  />
                  <p className="text-xs text-secondary mt-xs">
                    {t('locationForm.additionalTextCheckoutHelp')}
                  </p>
                </div>
              </div>

              {/* Custom Fields */}
              <div className="border-t border-primary pt-lg">
                <div className="flex items-center justify-between mb-md">
                  <h3 className="text-lg font-semibold text-primary">{t('locationForm.customFields')}</h3>
                  {formData.custom_fields.length < 2 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newField: CustomFieldConfig = {
                          key: `field_${Date.now()}`,
                          label: '',
                          type: 'text',
                          required: false,
                          maxLength: 200,
                        };
                        setFormData({ ...formData, custom_fields: [...formData.custom_fields, newField] });
                      }}
                      className="btn btn-ghost text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      {t('locationForm.addCustomField')}
                    </button>
                  )}
                </div>
                <p className="text-xs text-secondary mb-md">
                  {t('locationForm.customFieldsHelp')}
                </p>

                {formData.custom_fields.length === 0 && (
                  <p className="text-sm text-secondary italic">{t('locationForm.noCustomFields')}</p>
                )}

                <div className="space-y-lg">
                  {formData.custom_fields.map((field, index) => (
                    <div key={field.key} className="p-md border border-border rounded-lg space-y-md">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-primary">
                          {t('locationForm.customField')} {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = formData.custom_fields.filter((_, i) => i !== index);
                            setFormData({ ...formData, custom_fields: updated });
                          }}
                          className="btn-icon text-danger hover:bg-danger/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                        <div>
                          <label className="block text-sm font-medium text-primary mb-xs">
                            {t('locationForm.fieldKey')}
                          </label>
                          <input
                            type="text"
                            value={field.key}
                            onChange={(e) => {
                              const updated = [...formData.custom_fields];
                              updated[index] = { ...field, key: e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase() };
                              setFormData({ ...formData, custom_fields: updated });
                            }}
                            className="input w-full"
                            placeholder="e.g. hotel_name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-primary mb-xs">
                            {t('locationForm.fieldLabel')}
                          </label>
                          <textarea
                            value={labelHtmlToEditorText(field.label)}
                            onChange={(e) => {
                              const updated = [...formData.custom_fields];
                              updated[index] = { ...field, label: labelEditorTextToHtml(e.target.value) };
                              setFormData({ ...formData, custom_fields: updated });
                            }}
                            rows={2}
                            className="input w-full"
                            placeholder="e.g. Hotel Name"
                          />
                          <p className="text-xs text-secondary mt-xs">
                            {t('locationForm.fieldLabelHelp', { tags: CUSTOM_FIELD_ALLOWED_TAGS.map((tag) => `<${tag}>`).join(' ') })}
                          </p>
                        </div>
                      </div>

                      {(field.type === 'text' || field.type === 'textarea') && (
                        <div>
                          <label className="block text-sm font-medium text-primary mb-xs">
                            {t('locationForm.fieldPlaceholder')}
                          </label>
                          <textarea
                            value={field.placeholder || ''}
                            onChange={(e) => {
                              const updated = [...formData.custom_fields];
                              const { placeholder: _dropped, ...rest } = field;
                              // Stored only when set, so untouched configs keep their shape.
                              updated[index] = e.target.value ? { ...rest, placeholder: e.target.value } : rest;
                              setFormData({ ...formData, custom_fields: updated });
                            }}
                            rows={field.type === 'textarea' ? 3 : 1}
                            className="input w-full"
                            placeholder={t('locationForm.fieldPlaceholderExample')}
                          />
                          <p className="text-xs text-secondary mt-xs">
                            {field.type === 'textarea'
                              ? t('locationForm.fieldPlaceholderHelpTextarea')
                              : t('locationForm.fieldPlaceholderHelpText')}
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-md">
                        <div>
                          <label className="block text-sm font-medium text-primary mb-xs">
                            {t('locationForm.fieldType')}
                          </label>
                          <select
                            value={field.type}
                            onChange={(e) => {
                              const updated = [...formData.custom_fields];
                              const newType = e.target.value as CustomFieldType;
                              const keepsPlaceholder = newType === 'text' || newType === 'textarea';
                              const { placeholder, ...rest } = field;
                              updated[index] = {
                                ...rest,
                                ...(keepsPlaceholder && placeholder ? { placeholder } : {}),
                                type: newType,
                                // Each text kind gets its own default length; switching
                                // between them resets it so a 200-char limit never sticks
                                // to a multi-line field.
                                maxLength:
                                  newType === 'text' || newType === 'textarea'
                                    ? CUSTOM_FIELD_DEFAULTS[newType].maxLength
                                    : undefined,
                                rows: newType === 'textarea' ? (field.rows || CUSTOM_FIELD_DEFAULTS.textarea.rows) : undefined,
                                required: newType === 'info' ? false : field.required,
                              };
                              setFormData({ ...formData, custom_fields: updated });
                            }}
                            className="input w-full"
                          >
                            <option value="text">{t('locationForm.fieldTypeText')}</option>
                            <option value="textarea">{t('locationForm.fieldTypeTextarea')}</option>
                            <option value="date">{t('locationForm.fieldTypeDate')}</option>
                            <option value="datetime">{t('locationForm.fieldTypeDatetime')}</option>
                            <option value="info">{t('locationForm.fieldTypeInfo', 'Info (display only)')}</option>
                          </select>
                        </div>
                        {(field.type === 'text' || field.type === 'textarea') && (
                          <div>
                            <label className="block text-sm font-medium text-primary mb-xs">
                              {t('locationForm.fieldMaxLength')}
                            </label>
                            <NumberInput
                              value={field.maxLength || CUSTOM_FIELD_DEFAULTS[field.type].maxLength}
                              onChange={(v) => {
                                const updated = [...formData.custom_fields];
                                updated[index] = { ...field, maxLength: v || CUSTOM_FIELD_DEFAULTS[field.type as 'text' | 'textarea'].maxLength };
                                setFormData({ ...formData, custom_fields: updated });
                              }}
                              mode="int"
                              min={1}
                              max={CUSTOM_FIELD_DEFAULTS[field.type].maxLengthLimit}
                              className="input w-full"
                            />
                          </div>
                        )}
                        {field.type === 'textarea' && (
                          <div>
                            <label className="block text-sm font-medium text-primary mb-xs">
                              {t('locationForm.fieldRows')}
                            </label>
                            <NumberInput
                              value={field.rows || CUSTOM_FIELD_DEFAULTS.textarea.rows}
                              onChange={(v) => {
                                const updated = [...formData.custom_fields];
                                updated[index] = { ...field, rows: v || CUSTOM_FIELD_DEFAULTS.textarea.rows };
                                setFormData({ ...formData, custom_fields: updated });
                              }}
                              mode="int"
                              min={CUSTOM_FIELD_DEFAULTS.textarea.rowsMin}
                              max={CUSTOM_FIELD_DEFAULTS.textarea.rowsMax}
                              className="input w-full"
                            />
                            <p className="text-xs text-secondary mt-xs">{t('locationForm.fieldRowsHelp')}</p>
                          </div>
                        )}
                        {field.type !== 'info' && (
                          <div className="flex items-end">
                            <label className="flex items-center gap-md pb-sm">
                              <input
                                type="checkbox"
                                checked={field.required}
                                onChange={(e) => {
                                  const updated = [...formData.custom_fields];
                                  updated[index] = { ...field, required: e.target.checked };
                                  setFormData({ ...formData, custom_fields: updated });
                                }}
                                className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                              />
                              <span className="text-sm font-medium text-primary">{t('locationForm.fieldRequired')}</span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Translations for checkout text and custom field labels */}
              {availableLanguages.length > 1 && (formData.additional_text_checkout || formData.custom_fields.length > 0) && (
                <div className="border-t border-primary pt-lg">
                  <h3 className="text-lg font-semibold text-primary mb-md">{t('locationForm.checkoutTranslations')}</h3>

                  <div className="flex gap-sm mb-lg overflow-x-auto">
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setActiveLanguageTab(lang.code)}
                        className={`px-md py-sm rounded-md flex items-center gap-xs whitespace-nowrap transition-colors ${
                          activeLanguageTab === lang.code
                            ? 'bg-blue text-white'
                            : 'bg-secondary text-secondary hover:bg-primary/10'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="space-y-md">
                    {activeLanguageTab === defaultLanguage ? (
                      <div className="p-md bg-blue/5 border border-blue/20 rounded-lg">
                        {formData.additional_text_checkout && (
                          <div className="text-sm text-blue">
                            <strong>{t('locationForm.additionalTextCheckout')}:</strong>{' '}
                            <span dangerouslySetInnerHTML={{ __html: formData.additional_text_checkout }} />
                          </div>
                        )}
                        {formData.custom_fields.map((field) => (
                          <div key={field.key} className="text-sm text-blue mt-sm">
                            <strong>{field.key}:</strong>{' '}
                            <span dangerouslySetInnerHTML={{ __html: sanitizeCustomFieldHtml(field.label) }} />
                            {field.placeholder && (
                              <div className="text-xs text-secondary whitespace-pre-line mt-xs">
                                {t('locationForm.fieldPlaceholder')}: {field.placeholder}
                              </div>
                            )}
                          </div>
                        ))}
                        <p className="text-xs text-secondary mt-md">
                          {t('locationForm.mainLanguageEditAbove')}
                        </p>
                      </div>
                    ) : (
                      <>
                        {formData.additional_text_checkout && (
                          <div>
                            <label className="block text-sm font-medium text-primary mb-sm">
                              {t('locationForm.additionalTextCheckout')} ({activeLanguageTab.toUpperCase()})
                            </label>
                            <ReactQuill
                              value={translations[activeLanguageTab]?.additional_text_checkout || ''}
                              onChange={(value) => setTranslations(prev => ({
                                ...prev,
                                [activeLanguageTab]: {
                                  ...prev[activeLanguageTab],
                                  additional_text_checkout: value,
                                }
                              }))}
                              modules={quillModules}
                              formats={quillFormats}
                              theme="snow"
                              placeholder={`Add the ${availableLanguages.find(l => l.code === activeLanguageTab)?.label} version`}
                            />
                          </div>
                        )}

                        {formData.custom_fields.map((field) => {
                          const translated = translations[activeLanguageTab]?.custom_fields?.find(cf => cf.key === field.key);
                          const languageLabel = availableLanguages.find(l => l.code === activeLanguageTab)?.label;
                          // Patch one property of this field's translation, keeping the
                          // other (label / placeholder) as it is.
                          const updateTranslatedField = (patch: Partial<Omit<CustomFieldTranslation, 'key'>>) =>
                            setTranslations(prev => {
                              const prevCustomFields = prev[activeLanguageTab]?.custom_fields || [];
                              const existingIdx = prevCustomFields.findIndex(cf => cf.key === field.key);
                              const updatedCustomFields = [...prevCustomFields];
                              const current = existingIdx >= 0 ? prevCustomFields[existingIdx] : { key: field.key };
                              const next: CustomFieldTranslation = { ...current, ...patch, key: field.key };
                              if (!next.label) delete next.label;
                              if (!next.placeholder) delete next.placeholder;
                              if (existingIdx >= 0) {
                                updatedCustomFields[existingIdx] = next;
                              } else {
                                updatedCustomFields.push(next);
                              }
                              return {
                                ...prev,
                                [activeLanguageTab]: {
                                  ...prev[activeLanguageTab],
                                  custom_fields: updatedCustomFields,
                                }
                              };
                            });
                          return (
                            <div key={field.key} className="space-y-sm">
                              <div>
                                <label className="block text-sm font-medium text-primary mb-sm">
                                  {t('locationForm.fieldLabel')}: <span dangerouslySetInnerHTML={{ __html: sanitizeCustomFieldHtml(field.label) || field.key }} /> ({activeLanguageTab.toUpperCase()})
                                </label>
                                <textarea
                                  value={labelHtmlToEditorText(translated?.label)}
                                  onChange={(e) => updateTranslatedField({ label: labelEditorTextToHtml(e.target.value) })}
                                  rows={2}
                                  className="input w-full"
                                  placeholder={`Add the ${languageLabel} label`}
                                />
                              </div>
                              {(field.type === 'text' || field.type === 'textarea') && field.placeholder && (
                                <div>
                                  <label className="block text-sm font-medium text-primary mb-sm">
                                    {t('locationForm.fieldPlaceholder')} ({activeLanguageTab.toUpperCase()})
                                  </label>
                                  <textarea
                                    value={translated?.placeholder || ''}
                                    onChange={(e) => updateTranslatedField({ placeholder: e.target.value })}
                                    rows={field.type === 'textarea' ? 3 : 1}
                                    className="input w-full"
                                    placeholder={field.placeholder}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 3: Self-Service & Access */}
        {activeTab === 'selfservice' && (
          <div className="space-y-lg">
            {/* Self-Service Settings Card */}
            <div className="card">
              <h2 className="text-lg font-semibold text-primary mb-lg">{t('locationForm.selfServiceSettings')}</h2>
              <div className="space-y-lg">
                <div>
                  <label className="flex items-center gap-md">
                    <input
                      type="checkbox"
                      checked={formData.self_service_enabled}
                      onChange={(e) => setFormData({ ...formData, self_service_enabled: e.target.checked })}
                      className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                    />
                    <span className="text-sm font-medium text-primary">{t('locationForm.selfServiceEnabled')}</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('locationForm.gateCode')}
                  </label>
                  <input
                    type="text"
                    value={formData.gate_code}
                    onChange={(e) => setFormData({ ...formData, gate_code: e.target.value })}
                    className="input w-full"
                    placeholder={t('locationForm.gateCodePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('locationForm.gateLockBrand')}
                  </label>
                  <input
                    type="text"
                    value={formData.gate_lock_brand}
                    onChange={(e) => setFormData({ ...formData, gate_lock_brand: e.target.value })}
                    className="input w-full"
                    placeholder={t('locationForm.gateLockBrandPlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Email Delivery Settings Card */}
            <div className="card">
              <h2 className="text-lg font-semibold text-primary mb-lg">{t('locationForm.emailDeliverySettings')}</h2>
              <div className="space-y-lg">
                {/* Two-Step Email Delivery */}
                <div className="p-md bg-secondary rounded-lg border border-border">
                  <div className="flex items-start gap-md">
                    <input
                      type="checkbox"
                      checked={formData.two_emails_enabled}
                      onChange={(e) => setFormData({ ...formData, two_emails_enabled: e.target.checked })}
                      className="w-4 h-4 rounded border-primary text-blue focus:ring-blue mt-0.5"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-primary">{t('locationForm.twoStepEmailDelivery')}</span>
                      <p className="text-xs text-secondary mt-sm">
                        {t('locationForm.twoStepEmailEnabledDesc')}
                      </p>
                      <p className="text-xs text-secondary mt-xs">
                        {t('locationForm.twoStepEmailDisabledDesc')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cut-off settings - only shown when two_emails_enabled is true */}
                {formData.two_emails_enabled && (
                  <>
                    {/* Fixed Cut-off Schedule */}
                    <div className="border border-border rounded-lg p-md">
                      <h3 className="text-sm font-semibold text-primary mb-md">{t('locationForm.fixedCutoffSchedule')}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                        <div>
                          <label className="block text-sm font-medium text-primary mb-sm">
                            {t('locationForm.daysBeforeBooking')}
                          </label>
                          <NumberInput
                            value={formData.days_cut_off}
                            onChange={(v) => setFormData({ ...formData, days_cut_off: v || 0 })}
                            mode="int"
                            min={0}
                            className="input w-full"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-primary mb-sm">
                            {t('locationForm.time')}
                          </label>
                          <input
                            type="time"
                            value={formData.cut_off_codes}
                            onChange={(e) => setFormData({ ...formData, cut_off_codes: e.target.value })}
                            className="input w-full"
                          />
                        </div>
                      </div>
                      <div className="mt-md p-sm bg-blue/5 border border-blue/20 rounded-md">
                        <div className="flex items-start gap-sm">
                          <Info className="w-4 h-4 text-blue flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-secondary">
                            {t('locationForm.fixedCutoffExample')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Rolling Cut-off */}
                    <div className="border border-border rounded-lg p-md">
                      <h3 className="text-sm font-semibold text-primary mb-md">{t('locationForm.rollingCutoff')}</h3>
                      <div>
                        <label className="block text-sm font-medium text-primary mb-sm">
                          {t('locationForm.timeBeforeRentalStart')}
                        </label>
                        <div className="flex items-center gap-sm max-w-xs">
                          <NumberInput
                            value={
                              cutOffUnit === 'minutes'
                                ? Math.round((parseFloat(formData.time_cut_off) || 0) * 60)
                                : parseFloat(formData.time_cut_off) || 0
                            }
                            onChange={(v) => {
                              const hours = cutOffUnit === 'minutes' ? (v || 0) / 60 : (v || 0);
                              setFormData({ ...formData, time_cut_off: String(hours || '') });
                            }}
                            mode={cutOffUnit === 'minutes' ? 'int' : 'decimal'}
                            min={0}
                            className="input w-full"
                            placeholder="0"
                          />
                          <select
                            value={cutOffUnit}
                            onChange={(e) => setCutOffUnit(e.target.value as 'hours' | 'minutes')}
                            className="input w-auto flex-shrink-0"
                          >
                            <option value="hours">{t('locationForm.hours')}</option>
                            <option value="minutes">{t('locationForm.minutes')}</option>
                          </select>
                        </div>
                      </div>
                      <div className="mt-md p-sm bg-blue/5 border border-blue/20 rounded-md">
                        <div className="flex items-start gap-sm">
                          <Info className="w-4 h-4 text-blue flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-secondary">
                            {cutOffUnit === 'minutes'
                              ? t('locationForm.rollingCutoffExampleMinutes')
                              : t('locationForm.rollingCutoffExampleHours')
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab 4: Email Settings */}
        {activeTab === 'email' && (
          <div className="card">
            <h2 className="text-lg font-semibold text-primary mb-lg">{t('locationForm.tabEmail')}</h2>
            <div className="space-y-lg">
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.fromName')}
                </label>
                <input
                  type="text"
                  value={formData.from_name}
                  onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                  className="input w-full"
                  placeholder={t('locationForm.locationNamePlaceholder')}
                />
                <p className="text-xs text-secondary mt-xs">
                  {t('locationForm.fromNameHelp')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.replyToEmail')}
                </label>
                <input
                  type="email"
                  value={formData.reply_to_email}
                  onChange={(e) => setFormData({ ...formData, reply_to_email: e.target.value })}
                  className="input w-full"
                  placeholder="reply@example.com"
                />
                <p className="text-xs text-secondary mt-xs">
                  {t('locationForm.replyToEmailHelp')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tab 5: Tax Configuration */}
        {activeTab === 'tax' && (
          <div className="card">
            <h2 className="text-lg font-semibold text-primary mb-lg">{t('locationForm.tabTax')}</h2>
            <div className="space-y-lg">
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.taxRate')}
                </label>
                <NumberInput
                  value={formData.tax_rate * 100}
                  onChange={(v) => setFormData({ ...formData, tax_rate: (v || 0) / 100 })}
                  mode="decimal"
                  min={0}
                  max={100}
                  step={0.01}
                  className="input w-full"
                  placeholder="0.00"
                />
                <p className="text-xs text-secondary mt-xs">
                  {t('locationForm.taxRateHelp')}
                </p>
              </div>

              <div>
                <label className="flex items-center gap-md">
                  <input
                    type="checkbox"
                    checked={formData.tax_included}
                    onChange={(e) => setFormData({ ...formData, tax_included: e.target.checked })}
                    className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  />
                  <span className="text-sm font-medium text-primary">{t('locationForm.taxIncluded')}</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.taxName')}
                </label>
                <input
                  type="text"
                  value={formData.tax_name}
                  onChange={(e) => setFormData({ ...formData, tax_name: e.target.value })}
                  className="input w-full"
                  placeholder={t('locationForm.taxNamePlaceholder')}
                />
              </div>
            </div>
          </div>
        )}

        {/* Tab 6: Delivery Settings */}
        {activeTab === 'delivery' && (
          <div className="card">
            <h2 className="text-lg font-semibold text-primary mb-lg">{t('locationForm.tabDelivery')}</h2>
            <div className="space-y-lg">
              <div>
                <label className="flex items-center gap-md">
                  <input
                    type="checkbox"
                    checked={formData.deliveries}
                    onChange={(e) => setFormData({ ...formData, deliveries: e.target.checked })}
                    className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  />
                  <span className="text-sm font-medium text-primary">{t('locationForm.deliveriesEnabled')}</span>
                </label>
              </div>

              {formData.deliveries && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('locationForm.deliveryPreBookingHours')}
                    </label>
                    <NumberInput
                      value={formData.delivery_pre_booking_hours}
                      onChange={(v) => setFormData({ ...formData, delivery_pre_booking_hours: v || 0 })}
                      mode="int"
                      min={0}
                      className="input w-full"
                      placeholder="24"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('locationForm.deliveryTimeWindow')}
                    </label>
                    <NumberInput
                      value={formData.delivery_time_window_hours}
                      onChange={(v) => setFormData({ ...formData, delivery_time_window_hours: v || 0 })}
                      mode="int"
                      min={0}
                      className="input w-full"
                      placeholder="2"
                    />
                  </div>

                  {/* Pricing mode toggle: area-based vs variable (distance/time) */}
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('locationForm.pricingMode')}
                    </label>
                    <div className="space-y-sm">
                      <label className="flex items-start gap-md cursor-pointer">
                        <input
                          type="radio"
                          name="pricingMode"
                          checked={formData.delivery_pricing_type === null}
                          onChange={() => setFormData({ ...formData, delivery_pricing_type: null })}
                          className="mt-1"
                        />
                        <div>
                          <span className="text-sm font-medium text-primary block">{t('locationForm.pricingModeArea')}</span>
                          <span className="text-xs text-secondary">{t('locationForm.pricingModeAreaDesc')}</span>
                        </div>
                      </label>
                      <label className="flex items-start gap-md cursor-pointer">
                        <input
                          type="radio"
                          name="pricingMode"
                          checked={formData.delivery_pricing_type !== null}
                          onChange={() => setFormData({ ...formData, delivery_pricing_type: formData.delivery_pricing_type || 'distance' })}
                          className="mt-1"
                        />
                        <div>
                          <span className="text-sm font-medium text-primary block">{t('locationForm.pricingModeVariable')}</span>
                          <span className="text-xs text-secondary">{t('locationForm.pricingModeVariableDesc')}</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Area-based pricing: list of named areas, each with its own flat fee */}
                  {formData.delivery_pricing_type === null && (
                    <div>
                      <div className="flex items-center justify-between mb-sm">
                        <label className="block text-sm font-medium text-primary">
                          {t('locationForm.deliveryAreas')}
                        </label>
                        <button
                          type="button"
                          onClick={addDeliveryArea}
                          className="btn-icon btn-sm"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="space-y-md border border-border rounded-lg p-md">
                        {formData.delivery_areas.length === 0 && (
                          <p className="text-xs text-secondary italic">{t('locationForm.noDeliveryAreas')}</p>
                        )}
                        {formData.delivery_areas.map((area, index) => (
                          <div key={index} className="grid grid-cols-12 gap-sm items-center">
                            <div className="col-span-3">
                              <input
                                type="text"
                                value={area.id}
                                onChange={(e) => updateDeliveryArea(index, 'id', e.target.value)}
                                className="input text-sm w-full"
                                placeholder={t('locationForm.areaIdPlaceholder')}
                              />
                            </div>
                            <div className="col-span-5">
                              <input
                                type="text"
                                value={area.name}
                                onChange={(e) => updateDeliveryArea(index, 'name', e.target.value)}
                                className="input text-sm w-full"
                                placeholder={t('locationForm.areaNamePlaceholder')}
                              />
                            </div>
                            <div className="col-span-3">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={area.price}
                                onChange={(e) => updateDeliveryArea(index, 'price', parseFloat(e.target.value) || 0)}
                                className="input text-sm w-full"
                                placeholder={t('locationForm.price')}
                              />
                            </div>
                            <div className="col-span-1">
                              <button
                                type="button"
                                onClick={() => removeDeliveryArea(index)}
                                className="btn-icon btn-sm text-danger"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-secondary mt-xs">
                        {t('locationForm.deliveryAreasHelp')}
                      </p>
                    </div>
                  )}

                  {/* Variable pricing: distance/time × multiplication/brackets */}
                  {formData.delivery_pricing_type !== null && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-primary mb-sm">
                          {t('locationForm.deliveryPricingType')}
                        </label>
                        <select
                          value={formData.delivery_pricing_type}
                          onChange={(e) => setFormData({ ...formData, delivery_pricing_type: e.target.value as 'distance' | 'time' })}
                          className="input w-full"
                        >
                          <option value="distance">{t('locationForm.pricingTypeDistance')}</option>
                          <option value="time">{t('locationForm.pricingTypeTime')}</option>
                        </select>
                      </div>

                      {formData.delivery_pricing_type === 'distance' && (
                        <div>
                          <label className="block text-sm font-medium text-primary mb-sm">
                            {t('locationForm.distanceUnit')}
                          </label>
                          <select
                            value={formData.delivery_distance_unit}
                            onChange={(e) => setFormData({ ...formData, delivery_distance_unit: e.target.value as any })}
                            className="input w-full"
                          >
                            <option value="km">{t('locationForm.kilometers')}</option>
                            <option value="mi">{t('locationForm.miles')}</option>
                          </select>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-primary mb-sm">
                          {t('locationForm.deliveryPricingModel')}
                        </label>
                        <select
                          value={formData.delivery_pricing_model}
                          onChange={(e) => setFormData({ ...formData, delivery_pricing_model: e.target.value as any })}
                          className="input w-full"
                        >
                          <option value="multiplication">{t('locationForm.multiplication')}</option>
                          <option value="brackets">{t('locationForm.brackets')}</option>
                        </select>
                        <p className="text-xs text-secondary mt-xs">
                          {t('locationForm.deliveryPricingModelHelp')}
                        </p>
                      </div>

                      {formData.delivery_pricing_model === 'multiplication' && (
                        <div>
                          <label className="block text-sm font-medium text-primary mb-sm">
                            {formData.delivery_pricing_type === 'time'
                              ? t('locationForm.deliveryPricePerMinute')
                              : formData.delivery_distance_unit === 'mi'
                                ? t('locationForm.deliveryPricePerMile')
                                : t('locationForm.deliveryPricePerKm')}
                          </label>
                          <NumberInput
                            value={formData.delivery_price_per_unit}
                            onChange={(v) => setFormData({ ...formData, delivery_price_per_unit: v || 0 })}
                            mode="decimal"
                            min={0}
                            step={0.01}
                            className="input w-full"
                            placeholder="0.00"
                          />
                        </div>
                      )}

                      {formData.delivery_pricing_model === 'brackets' && (
                        <div>
                          <div className="flex items-center justify-between mb-sm">
                            <label className="block text-sm font-medium text-primary">
                              {t('locationForm.deliveryPriceBrackets')}
                            </label>
                            <button
                              type="button"
                              onClick={addPriceBracket}
                              className="btn-icon btn-sm"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="space-y-md border border-border rounded-lg p-md">
                            {formData.delivery_price_brackets.length === 0 && (
                              <p className="text-xs text-secondary italic">{t('locationForm.noPriceBrackets')}</p>
                            )}
                            {formData.delivery_price_brackets.map((bracket, index) => (
                              <div key={index} className="grid grid-cols-12 gap-sm items-center">
                                <div className="col-span-5">
                                  <div className="flex items-center gap-sm">
                                    <span className="text-xs text-secondary">{t('locationForm.max')}:</span>
                                    <input
                                      type="number"
                                      step="0.1"
                                      min="0"
                                      value={bracket.max}
                                      onChange={(e) => updatePriceBracket(index, 'max', parseFloat(e.target.value) || 0)}
                                      className="input text-sm w-full"
                                      placeholder={t('locationForm.maxDistancePlaceholder')}
                                    />
                                  </div>
                                </div>
                                <div className="col-span-6">
                                  <div className="flex items-center gap-sm">
                                    <span className="text-xs text-secondary">{t('locationForm.price')}:</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={bracket.price}
                                      onChange={(e) => updatePriceBracket(index, 'price', parseFloat(e.target.value) || 0)}
                                      className="input text-sm w-full"
                                      placeholder={t('locationForm.price')}
                                    />
                                  </div>
                                </div>
                                <div className="col-span-1">
                                  <button
                                    type="button"
                                    onClick={() => removePriceBracket(index)}
                                    className="btn-icon btn-sm text-danger"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-secondary mt-xs">
                            {t('locationForm.priceBracketsHelp')}
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-primary mb-sm">
                          {t('locationForm.deliveryMaxServiceArea')}
                        </label>
                        <NumberInput
                          value={formData.delivery_max_service_area}
                          onChange={(v) => setFormData({ ...formData, delivery_max_service_area: v || 0 })}
                          mode="decimal"
                          min={0}
                          className="input w-full"
                          placeholder="0"
                        />
                        <p className="text-xs text-secondary mt-xs">
                          {t('locationForm.deliveryMaxServiceAreaHelp')}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-primary mb-sm">
                          {t('locationForm.deliveryOrigin')}
                        </label>
                        <input
                          type="text"
                          value={formData.delivery_origin}
                          onChange={(e) => setFormData({ ...formData, delivery_origin: e.target.value })}
                          className="input w-full"
                          placeholder={t('locationForm.deliveryOriginPlaceholder')}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Tab 7: Confirmation Page */}
        {activeTab === 'confirmation' && (
          <div className="card">
            <h2 className="text-lg font-semibold text-primary mb-lg">{t('locationForm.tabConfirmation')}</h2>
            <div className="space-y-lg">
              {/* Pickup Instructions */}
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('locationForm.confirmationPickupText')}
                </label>
                <textarea
                  value={formData.confirmation_pickup_text}
                  onChange={(e) => setFormData({ ...formData, confirmation_pickup_text: e.target.value })}
                  className="input w-full"
                  rows={3}
                  placeholder={t('locationForm.confirmationPickupTextPlaceholder')}
                />
              </div>

              {/* Info Blocks */}
              <ConfirmationInfoBlocksEditor
                value={formData.confirmation_info_blocks}
                onChange={(val) => setFormData({ ...formData, confirmation_info_blocks: val })}
                label={t('locationForm.confirmationInfoBlocks')}
              />

              {/* Translations */}
              {availableLanguages.length > 1 && (
                <div className="border-t border-primary pt-lg">
                  <h3 className="text-lg font-semibold text-primary mb-md">{t('locationForm.translations')}</h3>

                  {/* Language tabs */}
                  <div className="flex gap-sm mb-lg overflow-x-auto">
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setActiveLanguageTab(lang.code)}
                        className={`px-md py-sm rounded-md flex items-center gap-xs whitespace-nowrap transition-colors ${
                          activeLanguageTab === lang.code
                            ? 'bg-blue text-white'
                            : 'bg-secondary text-secondary hover:bg-primary/10'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="space-y-lg">
                    {activeLanguageTab === defaultLanguage ? (
                      <div className="p-md bg-blue/5 border border-blue/20 rounded-lg">
                        <p className="text-sm text-blue">
                          <strong>{t('locationForm.confirmationPickupText')}:</strong>{' '}
                          {formData.confirmation_pickup_text || <em className="text-secondary">{t('common.none')}</em>}
                        </p>
                        <p className="text-xs text-secondary mt-md">
                          {t('locationForm.mainLanguageEditAbove')}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-primary mb-sm">
                            {t('locationForm.confirmationPickupText')} ({activeLanguageTab.toUpperCase()})
                          </label>
                          <textarea
                            value={translations[activeLanguageTab]?.confirmation_pickup_text || ''}
                            onChange={(e) => setTranslations(prev => ({
                              ...prev,
                              [activeLanguageTab]: {
                                ...prev[activeLanguageTab],
                                location_name: prev[activeLanguageTab]?.location_name || '',
                                pickup_address: prev[activeLanguageTab]?.pickup_address || '',
                                confirmation_pickup_text: e.target.value,
                                confirmation_info_blocks: prev[activeLanguageTab]?.confirmation_info_blocks || null,
                              }
                            }))}
                            className="input w-full"
                            rows={3}
                            placeholder={t('locationForm.confirmationPickupTextPlaceholder')}
                          />
                        </div>

                        <ConfirmationInfoBlocksEditor
                          value={translations[activeLanguageTab]?.confirmation_info_blocks || null}
                          onChange={(val) => setTranslations(prev => ({
                            ...prev,
                            [activeLanguageTab]: {
                              ...prev[activeLanguageTab],
                              location_name: prev[activeLanguageTab]?.location_name || '',
                              pickup_address: prev[activeLanguageTab]?.pickup_address || '',
                              confirmation_pickup_text: prev[activeLanguageTab]?.confirmation_pickup_text || '',
                              confirmation_info_blocks: val,
                            }
                          }))}
                          label={`${t('locationForm.confirmationInfoBlocks')} (${activeLanguageTab.toUpperCase()})`}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Single save action via the top "Save" button in page header */}
      </form>
    </div>
  );
}
